<template>
  <v-card class="ps-card" flat>
    <div class="ps-card__head">
      <v-icon small>mdi-account-group-outline</v-icon>
      Benutzer
      <span class="ps-card__hint">Zugänge, Berechtigungen &amp; Auszahlungen</span>
      <v-spacer />
      <v-btn @click="showAddUserDialog = true" color="primary" depressed small rounded>
        <v-icon small left>mdi-plus</v-icon>Benutzer
      </v-btn>
    </div>
    <v-card-text>
      <v-data-table
        :loading="usersLoading"
        :headers="headers"
        :items="users"
        class="ps-table"
      >
        <template v-slot:[`item.username`]="{ item }">
          <span class="ps-name">{{ item.username }}</span>
          <div class="ps-sub">{{ item.email }}</div>
        </template>
        <template v-slot:[`item.unpaidTasks`]="{ item }">
          <span
            v-if="item.unpaidTasks !== '-'"
            class="ps-chip"
            :class="{ 'ps-chip--primary': item.unpaidTasks > 0 }"
          >{{ item.unpaidTasks }}</span>
          <span v-else class="ps-muted">—</span>
        </template>
        <template v-slot:[`item.tasksPaid`]="{ item }">
          <span class="ps-price" :title="item.tasksPaid">{{ truncate(item.tasksPaid, 15) }}</span>
        </template>
        <template v-slot:[`item.permissions`]="{ item }">
          <span
            v-for="permission in item.permissions.slice(0, 4)"
            :key="permission"
            class="ps-chip"
            :class="{ 'ps-chip--primary': permission === 'ADMIN' }"
          >{{ permission }}</span>
          <v-tooltip v-if="item.permissions.length > 4" bottom>
            <template v-slot:activator="{ on }">
              <span class="ps-chip ps-chip--more" v-on="on">+{{ item.permissions.length - 4 }}</span>
            </template>
            <span>{{ item.permissions.slice(4).join(", ") }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.payout`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn icon small v-on="on" :disabled="!item.payment" @click="openPaymentDialog(item)">
                <v-icon small>mdi-cash-multiple</v-icon>
              </v-btn>
            </template>
            <span>Auszahlung</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn icon small v-on="on" @click="resetPassword(item.email)">
                <v-icon small>mdi-lock-reset</v-icon>
              </v-btn>
            </template>
            <span>Passwort zurücksetzen</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn small outlined rounded color="primary" @click="openEditUserDialog(item)">
            <v-icon small left>mdi-pencil</v-icon>Bearbeiten
          </v-btn>
        </template>
      </v-data-table>
    </v-card-text>
    <AddNewUserDialog
      :show="showAddUserDialog"
      :closeDialog="
        () => {
          showAddUserDialog = false;
        }
      "
    />
    <EditUserDialog
      :user="userToEdit"
      :close-dialog="
        () => {
          showEditUserDialog = false;
          userToEdit = null;
        }
      "
      :show="showEditUserDialog"
    />
    <PayUserDialog
      v-if="userToPay"
      :user="userToPay"
      :on-close="
        () => {
          userToPay = null;
        }
      "
    />
  </v-card>
</template>

<script>
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { showSnackbar } from "@/utils";
import AddNewUserDialog from "@/components/Settings/Users/AddNewUserDialog";
import PayUserDialog from "@/components/Settings/Users/PayUserDialog";
import { mapGetters } from "vuex";
import EditUserDialog from "@/components/Settings/Users/EditUserDialog";

export default {
  name: "AdminUsers",
  components: { PayUserDialog, AddNewUserDialog, EditUserDialog },
  data() {
    return {
      userToPay: null,
      userToEdit: null,
      showAddUserDialog: false,
      showEditUserDialog: false,
      headers: [
        { text: "Benutzer", align: "start", value: "username" },
        { text: "Offene Tasks", value: "unpaidTasks" },
        { text: "Gesamt ausgezahlt", value: "tasksPaid" },
        { text: "Berechtigungen", value: "permissions" },
        { text: "Aktionen", value: "payout", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    };
  },
  computed: {
    ...mapGetters(["allUsers", "usersLoading"]),
    users() {
      return this.allUsers.map((user) => {
        let tasksPaid = "-";
        if (user.payment && user.payment.transactionHistory) {
          tasksPaid = 0;
          user.payment.transactionHistory.forEach((transaction) => {
            tasksPaid += transaction?.totalPayed;
          });
        }
        return {
          tasksPaid:
            tasksPaid !== "-"
              ? tasksPaid + (user?.payment?.currency || "?")
              : "-",
          unpaidTasks: user.payment?.unpaidTasks?.length || "-",
          ...user,
        };
      });
    },
  },
  methods: {
    /** Lange Beträge (Float-Artefakte) kappen — voller Wert im Tooltip */
    truncate(value, max) {
      const str = String(value);
      return str.length > max ? str.slice(0, max) + "…" : str;
    },
    resetPassword(userEmail) {
      const auth = getAuth();
      sendPasswordResetEmail(auth, userEmail)
        .then(() => {
          showSnackbar({
            message: "Passwort-Reset-Mail versendet!",
            type: "success",
          });
        })
        .catch((error) => {
          showSnackbar({
            message: error.message,
            type: "error",
          });
        });
    },
    openPaymentDialog(user) {
      this.userToPay = user;
    },
    openEditUserDialog(user) {
      this.userToEdit = { ...user };
      this.showEditUserDialog = true;
    },
  },
};
</script>

<style scoped></style>
